/**
 * Correction tool for clocked time entries on a work order (#2705, #3680).
 *
 * Entries come from the work's Aloita / Tauko / Valmis buttons and the shift
 * clock; this surface has no timer of its own. Office staff open it to fix a
 * forgotten or wrong row and pick a service (`bookingServiceId`) for it. A
 * blank choice stays unassigned. This surface never patches planned hours or
 * prices.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  Tooltip,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { parseISO } from 'date-fns';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { FormDialog } from '@/components/common/FormDialog';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { toDateTimeLocal } from '@/components/workorders/WorkOrderCreateModal';
import {
  useCreateWorkOrderTimeEntry,
  useDeleteWorkOrderTimeEntry,
  useUpdateWorkOrderTimeEntry,
  useWorkOrderTimeEntries,
  type WorkOrderTimeEntry,
} from '@/hooks/useWorkOrderTimeEntries';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';
import { EDIT_BILLABLE_HOURS_PERMISSION } from '@/lib/billableHours';
import { formatDuration } from '@/utils/duration';
import { timeEntryDisplayHours } from '@/utils/actualTime';

export interface TimeEntryServiceOption {
  bookingServiceId: string;
  name: string;
}

interface WorkOrderTimeEntriesSectionProps {
  workOrderId: string;
  services: TimeEntryServiceOption[];
}

type EntryDraft = {
  bookingServiceId: string;
  startedAt: string;
  endedAt: string;
  description: string;
};

const EMPTY_DRAFT: EntryDraft = {
  bookingServiceId: '',
  startedAt: '',
  endedAt: '',
  description: '',
};

function serviceName(
  services: TimeEntryServiceOption[],
  bookingServiceId: string | null,
  unassignedLabel: string,
): string {
  if (bookingServiceId == null || bookingServiceId === '') return unassignedLabel;
  return services.find((service) => service.bookingServiceId === bookingServiceId)?.name
    ?? unassignedLabel;
}

function toIso(localValue: string): string | undefined {
  if (localValue.trim() === '') return undefined;
  const parsed = parseISO(localValue);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

function draftFromEntry(entry: WorkOrderTimeEntry): EntryDraft {
  return {
    bookingServiceId: entry.bookingServiceId ?? '',
    startedAt: toDateTimeLocal(new Date(entry.startedAt)),
    endedAt: entry.endedAt ? toDateTimeLocal(new Date(entry.endedAt)) : '',
    description: entry.description ?? '',
  };
}

/** Office-only (Mekaanikko holds `work_orders.update` but not this). */
export function WorkOrderTimeEntriesSection(props: WorkOrderTimeEntriesSectionProps) {
  return (
    <PermissionGuard permission={EDIT_BILLABLE_HOURS_PERMISSION}>
      <TimeEntriesCorrection {...props} />
    </PermissionGuard>
  );
}

function TimeEntriesCorrection({
  workOrderId,
  services,
}: WorkOrderTimeEntriesSectionProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { data: entries = [], isLoading } = useWorkOrderTimeEntries(workOrderId);
  const createEntry = useCreateWorkOrderTimeEntry(workOrderId);
  const updateEntry = useUpdateWorkOrderTimeEntry(workOrderId);
  const deleteEntry = useDeleteWorkOrderTimeEntry(workOrderId);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => { setNow(new Date()); }, 30_000);
    return () => window.clearInterval(id);
  }, []);

  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WorkOrderTimeEntry | null>(null);
  const [draft, setDraft] = useState<EntryDraft>(EMPTY_DRAFT);
  const [deleteTarget, setDeleteTarget] = useState<WorkOrderTimeEntry | null>(null);

  const openCount = entries.filter((entry) => entry.endedAt == null).length;

  const isDirty = dialogOpen && (
    editing == null
      ? draft.startedAt !== '' || draft.endedAt !== '' || draft.description !== '' || draft.bookingServiceId !== ''
      : JSON.stringify(draft) !== JSON.stringify(draftFromEntry(editing))
  );

  const busy = createEntry.isPending || updateEntry.isPending || deleteEntry.isPending;

  const openCreate = () => {
    setEditing(null);
    setDraft({
      ...EMPTY_DRAFT,
      startedAt: toDateTimeLocal(new Date()),
    });
    setDialogOpen(true);
  };

  const openEdit = (entry: WorkOrderTimeEntry) => {
    setEditing(entry);
    setDraft(draftFromEntry(entry));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setDraft(EMPTY_DRAFT);
  };

  const selectedServiceId = draft.bookingServiceId === '' ? null : draft.bookingServiceId;

  const handleSave = async () => {
    const startedAt = toIso(draft.startedAt);
    if (startedAt == null) return;
    const endedAt = toIso(draft.endedAt);
    try {
      if (editing) {
        const data: {
          startedAt: string;
          bookingServiceId: string | null;
          description: string | null;
          endedAt?: string;
        } = {
          startedAt,
          bookingServiceId: selectedServiceId,
          description: draft.description.trim() === '' ? null : draft.description.trim(),
        };
        if (endedAt !== undefined) data.endedAt = endedAt;
        await updateEntry.mutateAsync({ entryId: editing.id, data });
        notify.success(t('timeEntries.updateSuccess'));
      } else {
        const data: {
          startedAt: string;
          bookingServiceId: string | null;
          description?: string;
          endedAt?: string;
        } = {
          startedAt,
          bookingServiceId: selectedServiceId,
        };
        if (endedAt !== undefined) data.endedAt = endedAt;
        if (draft.description.trim() !== '') data.description = draft.description.trim();
        await createEntry.mutateAsync(data);
        notify.success(t('timeEntries.createSuccess'));
      }
      closeDialog();
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('timeEntries.saveFailed') }));
    }
  };

  const handleDelete = async () => {
    if (deleteTarget == null) return;
    try {
      await deleteEntry.mutateAsync(deleteTarget.id);
      notify.success(t('timeEntries.deleteSuccess'));
      setDeleteTarget(null);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('timeEntries.deleteFailed') }));
    }
  };

  const serviceSelect = (
    value: string,
    onChange: (next: string) => void,
    label: string,
    id: string,
  ) => (
    <FormControl size="small" fullWidth>
      <InputLabel id={id}>{label}</InputLabel>
      <Select
        labelId={id}
        label={label}
        value={value}
        onChange={(event) => { onChange(event.target.value); }}
        inputProps={{ 'aria-label': label }}
      >
        <MenuItem value="">{t('timeEntries.unassigned')}</MenuItem>
        {services.map((service) => (
          <MenuItem key={service.bookingServiceId} value={service.bookingServiceId}>
            {service.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ mt: 3 }} data-testid="work-order-time-entries">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          onClick={() => { setExpanded((current) => !current); }}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          aria-expanded={expanded}
          data-testid="time-entries-toggle"
        >
          {t('timeEntries.toggle', { count: entries.length })}
        </Button>
        {openCount > 0 ? (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={t('timeEntries.openCount', { count: openCount })}
          />
        ) : null}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, my: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">{t('timeEntries.hint')}</Typography>
          <PermissionGuard permission="work_orders.update">
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={busy}
              data-testid="time-entry-add"
            >
              {t('timeEntries.add')}
            </Button>
          </PermissionGuard>
        </Box>

        {isLoading ? (
          <Typography variant="body2" color="text.secondary">{tc('loading')}</Typography>
        ) : entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('timeEntries.empty')}</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('timeEntries.mechanic')}</TableCell>
                <TableCell>{t('timeEntries.service')}</TableCell>
                <TableCell>{t('timeEntries.startedAt')}</TableCell>
                <TableCell>{t('timeEntries.endedAt')}</TableCell>
                <TableCell align="right">{t('timeEntries.duration')}</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => {
                const open = entry.endedAt == null;
                return (
                  <TableRow key={entry.id} data-testid={`time-entry-row-${entry.id}`}>
                    <TableCell>{entry.mechanicName ?? '—'}</TableCell>
                    <TableCell>
                      {serviceName(services, entry.bookingServiceId, t('timeEntries.unassigned'))}
                    </TableCell>
                    <TableCell>{new Date(entry.startedAt).toLocaleString()}</TableCell>
                    <TableCell>
                      {open ? t('timeEntries.open') : new Date(entry.endedAt ?? '').toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {formatDuration(timeEntryDisplayHours(entry, now))}
                    </TableCell>
                    <TableCell padding="checkbox">
                      <PermissionGuard permission="work_orders.update">
                        <Tooltip title={t('timeEntries.edit')}>
                          <IconButton
                            size="small"
                            onClick={() => { openEdit(entry); }}
                            aria-label={t('timeEntries.edit')}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={tc('delete')}>
                          <IconButton
                            size="small"
                            onClick={() => { setDeleteTarget(entry); }}
                            aria-label={tc('delete')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </PermissionGuard>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Collapse>

      <FormDialog
        open={dialogOpen}
        onClose={closeDialog}
        title={editing ? t('timeEntries.edit') : t('timeEntries.add')}
        isDirty={isDirty}
        onSubmit={() => { void handleSave(); }}
        isLoading={createEntry.isPending || updateEntry.isPending}
        submitDisabled={draft.startedAt.trim() === ''}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, pt: 1 }}>
          {serviceSelect(
            draft.bookingServiceId,
            (next) => { setDraft((current) => ({ ...current, bookingServiceId: next })); },
            t('timeEntries.service'),
            'entry-service',
          )}
          <TextField
            type="datetime-local"
            size="small"
            label={t('timeEntries.startedAt')}
            value={draft.startedAt}
            onChange={(event) => {
              setDraft((current) => ({ ...current, startedAt: event.target.value }));
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="datetime-local"
            size="small"
            label={t('timeEntries.endedAt')}
            value={draft.endedAt}
            onChange={(event) => {
              setDraft((current) => ({ ...current, endedAt: event.target.value }));
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            label={t('timeEntries.description')}
            value={draft.description}
            onChange={(event) => {
              setDraft((current) => ({ ...current, description: event.target.value }));
            }}
          />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={deleteTarget != null}
        title={t('timeEntries.deleteConfirmTitle')}
        message={t('timeEntries.deleteConfirmMessage')}
        confirmColor="error"
        onConfirm={() => { void handleDelete(); }}
        onCancel={() => { setDeleteTarget(null); }}
        loading={deleteEntry.isPending}
      />
    </Box>
  );
}
